// Pieces shared by the meal pages: week from the URL and the side chooser.
import React, { useState } from 'react';
import {
  Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton,
  ListItemIcon, ListItemText,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import * as T from '../types';
import { frequencyText, number, servings, shortMeal, today, weekStart } from '../labels';

export const PLAN_PATH = '/casa/refeicoes';

/** Monday of the week in "?semana=" (any day of it), or of this week. */
export function useWeekParam(): T.DateOnly {
  const [params] = useSearchParams();
  const value = params.get('semana');
  return weekStart(value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : today());
}

export const planUrl = (week: T.DateOnly) => `${PLAN_PATH}?semana=${week}`;

const COURSE_PREFIX: Partial<Record<T.RecipeCourse, string>> = { Side: 'Acompanhamento', Starter: 'Entrada', Dessert: 'Sobremesa' };

/** "Acompanhamento · Habitual · 20 min · rápida · dá sobras · 5 ingredientes". */
export function recipeSubtitle(r: T.RecipeSummary): string {
  const parts = [frequencyText(r.frequency)];
  const course = COURSE_PREFIX[r.course];
  if (course) parts.unshift(course);
  if (r.prepMinutes !== null && r.prepMinutes !== undefined) parts.push(`${r.prepMinutes} min`);
  if (r.isQuick) parts.push('rápida');
  if (r.yieldsLeftovers) parts.push('dá sobras');
  parts.push(r.ingredientCount === 1 ? '1 ingrediente' : `${r.ingredientCount} ingredientes`);
  return parts.join(' · ');
}

export const leftoverSourceText = (s: T.LeftoverSource) =>
  `${s.recipeName} · ${shortMeal(s.date, s.mealType)}${s.yieldsLeftovers ? '' : ' (não dá sobras)'}`;

/** "Bolonhesa · 3 doses · 12/10". */
export function frozenPickerText(b: T.FrozenBatch): string {
  const [, m, d] = b.frozenOn.split('-');
  const place = b.locationName ? ` · ${b.locationName.split(' › ')[0]}` : '';
  return `${b.recipeName} · ${servings(b.remaining)} · ${d}/${m}${place}`;
}

export const decimalText = (value?: number | null) => (value === null || value === undefined ? '' : number(value, 2));

export const asSide = (r: T.RecipeSummary): T.RecipeSide => ({ publicId: r.publicId, name: r.name });

/** Choosing several side dishes: tick/untick, "Sem acompanhamento", "Feito". `options` come first, in order. Mount it when opening. */
export function SideChooserDialog({ open, current, options, onClose }: {
  open: boolean; current: T.RecipeSide[]; options: T.RecipeSide[]; onClose: (chosen: T.RecipeSide[] | null) => void;
}) {
  const [chosen, setChosen] = useState<T.RecipeSide[]>(current);
  const all = options.concat(current.filter(c => options.every(o => o.publicId !== c.publicId)));
  const toggle = (side: T.RecipeSide) =>
    setChosen(list => (list.some(s => s.publicId === side.publicId) ? list.filter(s => s.publicId !== side.publicId) : list.concat(side)));

  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Acompanhamentos</DialogTitle>
      {all.length === 0 ? (
        <>
          <DialogContent>
            <Alert severity="info">Ainda não há acompanhamentos. Cria receitas do tipo «Acompanhamento» (arroz, batata cozida, salada…).</Alert>
          </DialogContent>
          <DialogActions><Button onClick={() => onClose(null)}>OK</Button></DialogActions>
        </>
      ) : (
        <>
          <DialogContent dividers sx={{ p: 0 }}>
            <List dense>
              {all.map(side => {
                const on = chosen.some(s => s.publicId === side.publicId);
                return (
                  <ListItemButton key={side.publicId} onClick={() => toggle(side)}>
                    <ListItemIcon sx={{ minWidth: 36 }}><Checkbox edge="start" checked={on} tabIndex={-1} disableRipple /></ListItemIcon>
                    <ListItemText primary={side.name} />
                  </ListItemButton>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap' }}>
            <Button onClick={() => onClose([])} sx={{ mr: 'auto' }}>Sem acompanhamento</Button>
            <Button onClick={() => onClose(null)}>Cancelar</Button>
            <Button variant="contained" onClick={() => onClose(chosen)}>Feito</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
